import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, tap, throwError, timeout } from 'rxjs';
import { NetworkConnectionService } from './network-connection.service';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  countRequests = 0; // support for bring app online
  private static readonly SECRET_QUERY_KEYS = /^(password|passwd|pwd|access_token|client_secret|secret)$/i;
  constructor(private networkConnectionService: NetworkConnectionService,  private toastr: ToastrService,) {}

  private stripSecretsFromUrl(req: HttpRequest<any>): HttpRequest<any> {
    // SignalR negotiate/SSE may put access_token on /hubs/ query; leave those.
    if (req.url.indexOf('/hubs/') >= 0) {
      return req;
    }
    // Live download actions still require ?password= until the API is redeployed.
    if (req.url.indexOf('RequestForensicNotebook') >= 0
      || req.url.indexOf('DownloadForensicNote') >= 0) {
      return req;
    }

    let stripped = false;
    let params = req.params;
    for (const key of req.params.keys()) {
      if (HttpRequestInterceptor.SECRET_QUERY_KEYS.test(key)) {
        params = params.delete(key);
        stripped = true;
      }
    }

    let parsed: URL;
    try {
      parsed = new URL(req.urlWithParams, 'https://fn-local.invalid');
    } catch {
      return stripped ? req.clone({ params }) : req;
    }

    const keys = Array.from(parsed.searchParams.keys());
    for (const key of keys) {
      if (HttpRequestInterceptor.SECRET_QUERY_KEYS.test(key)) {
        parsed.searchParams.delete(key);
        stripped = true;
      }
    }

    if (!stripped) {
      return req;
    }

    let newParams = new HttpParams();
    parsed.searchParams.forEach((value, key) => {
      if (!HttpRequestInterceptor.SECRET_QUERY_KEYS.test(key)) {
        newParams = newParams.append(key, value);
      }
    });

    const cleanUrl = req.url.indexOf('://') >= 0
      ? parsed.origin + parsed.pathname
      : parsed.pathname;

    return req.clone({ url: cleanUrl, params: newParams });
  }
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.stripSecretsFromUrl(req);
    if(!!['assets', 'tinymce'].find(e=> req.url.includes(e))) {
      return next.handle(req);
    }
    // exclude requests
    if(['Attachments/GetImage', 'Ai/CreateTranscription'].find(e=>req.url.includes(e))) {
      return next.handle(req);
    }
    const start = performance.now();
    return next.handle(req).pipe(
      tap(()=> {
        const end = performance.now();
        const responseTime = end - start;
        if(!this.networkConnectionService.isOnline && responseTime < 1000) {
          this.countRequests += 1;
          if(this.countRequests > 5) {
            this.networkConnectionService.setAppOnline();
          }
        }
      }),
      timeout(60000),
      catchError((error: Error) => {
        if (error.name === 'TimeoutError') {
          this.countRequests = 0;
          if(this.networkConnectionService.isOnline) {
            this.networkConnectionService.setAppOffline();
          }
          return throwError(() => 'Network is offline'); // Handle timeout error
        }
        return throwError(() => error); // Forward other errors
      })
    );
  }
}
